import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import ReactTooltip from 'react-tooltip';
import classnames from 'classnames';

import AgGrid from '../../shared/agGrid/agGrid.component';

import WarningBanner from '../shared/warningBanner.component';
import { AllTabBookTypes, BookTypePills } from '../../shared/bookTypePills';
import withPillNavigationDecorator from '../../shared/withPillNavigation.decorator';

import components from './frameworkComponents';
import { getRowHeight } from './helpers';
import { naturalCompare } from '@common/utils/naturalCompare';
import { ColumnDefinitionPropTypes } from '../../shared/propTypes/common';

const rebuildReactTooltip = debounce(ReactTooltip.rebuild, 200);

const comparator = (valueA, valueB, nodeA, nodeB, isDescending) => {
  if (nodeA.data.bookType === nodeB.data.bookType) {
    const valueAParsed = typeof valueA.data === 'string' ? valueA.data : valueA.data.value;
    const valueBParsed = typeof valueB.data === 'string' ? valueB.data : valueB.data.value;
    if (valueA.type === 'DATA_BORDER_DOUBLEBOTTOMLINE') {
      return isDescending ? -1 : 1;
    }
    if (valueB.type === 'DATA_BORDER_DOUBLEBOTTOMLINE') {
      return isDescending ? 1 : -1;
    }
    return naturalCompare(valueAParsed ?? 0, valueBParsed ?? 0);
  }

  return (
    AllTabBookTypes.indexOf(nodeA.data.bookType) - AllTabBookTypes.indexOf(nodeB.data.bookType)
  ) * (isDescending ? -1 : 1);
};

export class BalanceSheetGridReport extends PureComponent {
  static propTypes = {
    columnDefinitions: PropTypes.arrayOf(ColumnDefinitionPropTypes),
    reportData: PropTypes.arrayOf(PropTypes.shape({
      rowType: PropTypes.string,
      description: PropTypes.string,
      columns: PropTypes.objectOf(PropTypes.object), // eslint-disable-line react/forbid-prop-types
    })),
    chosenValuesText: PropTypes.string.isRequired,
    reportTitle: PropTypes.string.isRequired,
    isFetchingReportData: PropTypes.bool,
    multipleCurrenciesWarning: PropTypes.string,
    tbeErrorWarning: PropTypes.string,

    onCellValueChanged: PropTypes.func,
    isRowShadingEnabled: PropTypes.bool,
    warningMessage: PropTypes.node,
  };

  static defaultProps = {
    reportData: [],
    isFetchingReportData: false,
    isRowShadingEnabled: false,
    onCellValueChanged: null,
    isSortingEnabled: true,
  };

  state = {
    directTaxEntryCancelling: false,
    columnDefinitions: [],
  };

  componentDidMount() {
    document.addEventListener('keydown', this.onKeyDown);
    this.setState({
      columnDefinitions: this.props.columnDefinitions,
    });
  }

  componentDidUpdate = prevProps => {
    if (prevProps.columnDefinitions !== this.props.columnDefinitions) {
      if (this.state.gridApi) {
        this.state.gridApi.stopEditing();
      }
      this.setState({
        columnDefinitions: this.props.columnDefinitions,
      });
    }

    if (prevProps.isFetchingReportData !== this.props.isFetchingReportData && !this.props.isFetchingReportData) {
      rebuildReactTooltip();
    }
  };

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown);
  }

  onCellValueChanged = params => {
    const { onCellValueChanged } = this.props;

    if (onCellValueChanged) {
      onCellValueChanged(params);
    }
  };

  onCellEditingStopped = params => {
    const { directTaxEntryCancelling } = this.state;
    const currentCell = params.api.getFocusedCell();

    const editedCellIsFocusedCell = currentCell && currentCell.rowIndex === params.rowIndex
      && currentCell.column.colId === params.column.colId;
    if (!editedCellIsFocusedCell) {
      return;
    }
    const lastRowIndex = params.api.paginationGetRowCount() - 1;

    if (lastRowIndex !== currentCell.rowIndex && !directTaxEntryCancelling) {
      params.api.setFocusedCell(currentCell.rowIndex + 1, currentCell.column);
    }

    if (directTaxEntryCancelling) {
      this.setState({ directTaxEntryCancelling: false });
    }
  };

  onCellFocused = params => {
    const focusedCell = params.api.getFocusedCell();
    if (!focusedCell) {
      return;
    }

    params.api.startEditingCell({
      rowIndex: focusedCell.rowIndex,
      colKey: focusedCell.column.colId,
    });
  };

  onKeyDown = event => {
    if (event.key === 'Escape' || event.code === 'Escape') {
      this.setState({ directTaxEntryCancelling: true });
    }
  };

  onGridReady = ({ api }) => {
    api.addEventListener('bodyScroll', rebuildReactTooltip);
    document.addEventListener('keydown', this.onKeyDown);
    this.gridApi = api;
  };

  render() {
    const {
      reportData,
      reportTitle,
      chosenValuesText,
      isFetchingReportData,
      isRowShadingEnabled,
      multipleCurrenciesWarning,
      tbeErrorWarning,
      warningMessage,
      ...rest
    } = this.props;

    const {
      columnDefinitions,
    } = this.state;

    return (
      <div className="grid-report">
        <div className="row no-gutters">
          <div className="col">
            <div className="d-flex flex-row justify-content-between">
              <h5>{reportTitle}</h5>
            </div>
          </div>
        </div>
        <div className="row no-gutters">
          <div className="col">
            <div className="d-flex flex-row justify-content-start chosenValuesText">
              <div>
                {chosenValuesText}
              </div>
              <div className="data-warning">
                {multipleCurrenciesWarning}
              </div>
              <div className="data-warning">
                {tbeErrorWarning}
              </div>
            </div>
          </div>
        </div>
        {warningMessage
          ? (
            <div className="row">
              <div className="col">
                <WarningBanner warningMessage={warningMessage} />
              </div>
            </div>
          )
          : null}
        <div className={classnames({ 'report-grid-body-shaded': isRowShadingEnabled }, 'ag-grid-full-page-with-secondary-menu')}>
          <AgGrid
            defaultColDef={{ comparator }}
            columnDefs={columnDefinitions}
            rowData={reportData}
            headerHeight={80}
            rowClass="grid-report-row"
            getRowHeight={getRowHeight}
            suppressRowHoverHighlight
            suppressColumnHoverHighlight
            suppressContextMenu
            suppressCellFocus={false}
            suppressRowClickSelection
            suppressSizeColumnsToFit
            enableSorting={false}
            isGridLoading={isFetchingReportData}
            onGridReady={this.onGridReady}
            allowRowShadowing
            components={components}
            enterNavigatesVertically
            enterNavigatesVerticallyAfterEdit
            onCellValueChanged={this.onCellValueChanged}
            singleClickEdit
            onCellFocused={this.onCellFocused}
            onCellEditingStopped={this.onCellEditingStopped}
            {...rest}
          />
          <ReactTooltip place="right" effect="float" id="unapprovedBalanceIndicator" />
        </div>
      </div>
    );
  }
}

export default withPillNavigationDecorator(
  ({ bookTypes }) => [
    ...BookTypePills.filter(({ type }) => bookTypes.includes(type)),
    ...(bookTypes.includes('A') ? [{ label: 'ALL', type: 'ALL' }] : []),
  ],
  {
    propTypes: {
      bookType: PropTypes.string.isRequired,
      bookTypes: PropTypes.arrayOf(PropTypes.string),
      selectBookType: PropTypes.func.isRequired,
    },
    getActivePill: props => props.bookType,
    onPillChangeEventPropKey: 'selectBookType',
  }
)(BalanceSheetGridReport);
